import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Avatar,
  useTheme,
  alpha,
  Fade,
  Slide,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  Refresh,
  FilterList,
  Download,
  Insights,
  Timeline,
  PieChart,
  BarChart,
  Star,
  EmojiEvents,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyticsService, AnalyticsMetrics, ClassPerformance, WeeklyAttendanceData } from '../../services/analyticsService';

interface MetricCardProps {
  title: string;
  value: string | number;
  change: number;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  delay?: number;
}

function MetricCard({ title, value, change, icon, color, loading = false, delay = 0 }: MetricCardProps) {
  const theme = useTheme();
  const isPositive = change >= 0;
  
  return (
    <Fade in timeout={800} style={{ transitionDelay: `${delay}ms` }}>
      <Card
        sx={{
          height: '100%',
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            boxShadow: `0 20px 40px -12px ${alpha(color, 0.4)}`,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%)`,
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          '&:hover::before': {
            opacity: 1,
          },
        }}
      >
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.common.white, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
              }}
            >
              {React.cloneElement(icon as React.ReactElement<any>, { 
                sx: { fontSize: 28 } 
              })}
            </Box>
            {!loading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                }}
              >
                {isPositive ? (
                  <TrendingUpIcon sx={{ fontSize: 16 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16 }} />
                )}
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {isPositive ? '+' : ''}{change}%
                </Typography>
              </Box>
            )}
          </Box>
          
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 1,
              fontSize: '2.5rem',
              lineHeight: 1,
              background: loading ? 'none' : `linear-gradient(135deg, white 0%, ${alpha(theme.palette.common.white, 0.8)} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
            }}
          >
            {loading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : value}
          </Typography>
          
          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, fontSize: '0.875rem' }}>
            {title}
          </Typography>

          {/* Decorative element */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              right: -20,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />
        </CardContent>
      </Card>
    </Fade>
  );
}

interface ChartPlaceholderProps {
  title: string;
  height?: number;
  icon?: React.ReactNode;
  description?: string;
}

function ChartPlaceholder({ title, height = 300, icon, description }: ChartPlaceholderProps) {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.mode === 'light'
          ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          : 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 40%),
            radial-gradient(circle at 80% 80%, ${alpha(theme.palette.common.white, 0.05)} 0%, transparent 40%)
          `,
          pointerEvents: 'none',
        }}
      />
      
      <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', color: 'white' }}>
        {icon && (
          <Box sx={{ mb: 2, opacity: 0.8 }}>
            {React.cloneElement(icon as React.ReactElement<any>, { 
              sx: { fontSize: 48 } 
            })}
          </Box>
        )}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.9 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ opacity: 0.7, maxWidth: 300 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function Analytics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [timeRange, setTimeRange] = useState('week');
  const [selectedClass, setSelectedClass] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Real data states
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [classPerformance, setClassPerformance] = useState<ClassPerformance[]>([]);
  const [weeklyData, setWeeklyData] = useState<WeeklyAttendanceData[]>([]);
  const [topPerformers, setTopPerformers] = useState<ClassPerformance[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, selectedClass]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [metricsData, classPerformanceData, weeklyAttendanceData, topPerformersData] = await Promise.all([
        AnalyticsService.getAnalyticsMetrics(timeRange),
        AnalyticsService.getClassPerformance(10),
        AnalyticsService.getWeeklyAttendanceData(),
        AnalyticsService.getTopPerformingClasses(3)
      ]);

      setMetrics(metricsData);
      setClassPerformance(classPerformanceData);
      setWeeklyData(weeklyAttendanceData);
      setTopPerformers(topPerformersData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalyticsData();
    setRefreshing(false);
  };

  const metricsData = metrics ? [
    {
      title: 'Overall Attendance Rate',
      value: `${metrics.overallAttendanceRate}%`,
      change: metrics.changePercentages.attendanceChange,
      icon: <AssessmentIcon />,
      color: theme.palette.primary.main,
    },
    {
      title: 'Active Students',
      value: metrics.activeStudents.toString(),
      change: metrics.changePercentages.studentsChange,
      icon: <PersonIcon />,
      color: theme.palette.success.main,
    },
    {
      title: 'Classes Conducted',
      value: metrics.classesCount.toString(),
      change: metrics.changePercentages.classesChange,
      icon: <SchoolIcon />,
      color: theme.palette.info.main,
    },
    {
      title: 'Average Daily Rate',
      value: `${metrics.avgDailyRate}%`,
      change: metrics.changePercentages.dailyRateChange,
      icon: <CalendarIcon />,
      color: theme.palette.warning.main,
    },
  ] : [];

  const timeRanges = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'semester', label: 'This Semester' },
    { value: 'year', label: 'This Year' },
  ];

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button onClick={loadAnalyticsData} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced Header */}
      <Box
        sx={{
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          background: theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #434343 0%, #000000 100%)',
          color: 'white',
          p: isMobile ? 3 : 4,
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }}
        />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          position: 'relative', 
          zIndex: 1,
          gap: isMobile ? 3 : 0
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                }}
              >
                <Insights sx={{ fontSize: isMobile ? 24 : 28 }} />
              </Box>
              <Typography
                variant={isMobile ? "h4" : "h3"}
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, white 0%, rgba(255,255,255,0.8) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  lineHeight: 1.2,
                }}
              >
                Analytics Dashboard
              </Typography>
            </Box>
            <Typography variant={isMobile ? "body1" : "h6"} sx={{ opacity: 0.9, fontWeight: 400 }}>
              Real-time insights and performance metrics for attendance tracking
            </Typography>
          </Box>
          
          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                borderColor: alpha(theme.palette.common.white, 0.3),
                color: 'white',
                backdropFilter: 'blur(10px)',
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  borderColor: alpha(theme.palette.common.white, 0.5),
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                },
              }}
            >
              Refresh Data
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.15),
                color: 'white',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.25),
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Export Report
            </Button>
          </Stack>
        </Box>
      </Box>

      {user?.role !== 'admin' && (
        <Slide direction="down" in timeout={600}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Typography variant="body2">
              📊 Analytics shown for your assigned classes only. Contact admin for full system access.
            </Typography>
          </Alert>
        </Slide>
      )}

      {/* Enhanced Filters */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 4,
          background: theme.palette.mode === 'light'
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[50], 0.8)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.8)} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FilterList sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filter Options
          </Typography>
        </Box>
        
        <Stack spacing={3}>
          <Stack direction={isMobile ? "column" : "row"} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  {timeRanges.map((range) => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            {user?.role === 'admin' && (
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Class Filter</InputLabel>
                  <Select
                    value={selectedClass}
                    label="Class Filter"
                    onChange={(e) => setSelectedClass(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                  >
                    <MenuItem value="all">All Classes</MenuItem>
                    {classPerformance.map((cls) => (
                      <MenuItem key={cls.name} value={cls.name.toLowerCase()}>
                        {cls.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            
            <Box sx={{ flex: isMobile ? 1 : 'none' }}>
              <Stack direction={isMobile ? "column" : "row"} spacing={1}>
                <Button
                  variant={timeRange === 'week' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setTimeRange('week')}
                  sx={{ borderRadius: 2 }}
                >
                  Weekly
                </Button>
                <Button
                  variant={timeRange === 'month' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setTimeRange('month')}
                  sx={{ borderRadius: 2 }}
                >
                  Monthly
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Enhanced Key Metrics */}
      <Stack 
        direction={isMobile ? "column" : "row"} 
        spacing={3} 
        sx={{ mb: 4 }}
      >
        {metricsData.map((metric, index) => (
          <Box key={index} sx={{ flex: 1 }}>
            <MetricCard {...metric} loading={loading} delay={index * 100} />
          </Box>
        ))}
      </Stack>

      <Stack direction={isMobile ? "column" : "row"} spacing={3}>
        {/* Enhanced Attendance Trends Chart */}
        <Box sx={{ flex: isMobile ? "none" : "1 1 60%" }}>
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              background: theme.palette.mode === 'light'
                ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.grey[50], 0.9)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Timeline sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Attendance Trends
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                  <BarChart sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton size="small" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                  <PieChart sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
            <ChartPlaceholder 
              title="Interactive Attendance Chart"
              height={350}
              icon={<Timeline />}
              description="Advanced charts with drill-down capabilities and real-time updates"
            />
          </Paper>
        </Box>

        {/* Enhanced Class Performance */}
        <Box sx={{ flex: isMobile ? "none" : "1 1 40%" }}>
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(circle at 20% 80%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%)
                `,
                pointerEvents: 'none',
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SchoolIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Class Performance
                </Typography>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: 'white' }} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {classPerformance.slice(0, 5).map((cls, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {cls.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {cls.attendance}%
                          </Typography>
                          {cls.trend === 'up' ? (
                            <TrendingUpIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }} />
                          )}
                        </Box>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={cls.attendance} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.common.white, 0.2),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: cls.attendance >= 90 ? '#4ade80' : 
                                    cls.attendance >= 80 ? '#fbbf24' : '#ef4444',
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {cls.students} students
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {cls.improvement > 0 ? '+' : ''}{cls.improvement}% vs last week
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Stack>

      <Stack direction={isMobile ? "column" : "row"} spacing={3} sx={{ mt: 3 }}>
        {/* Enhanced Weekly Overview */}
        <Box sx={{ flex: 1 }}>
          <Paper 
            sx={{ 
              p: 3,
              borderRadius: 3,
              background: theme.palette.mode === 'light'
                ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.grey[50], 0.9)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CalendarIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Weekly Overview
              </Typography>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
                {weeklyData.map((dayData, index) => {
                  const attendance = dayData.attendance;
                  return (
                    <Box 
                      key={dayData.day}
                      sx={{ 
                        textAlign: 'center',
                        transition: 'transform 0.3s ease',
                        flex: 1,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                        }
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {dayData.day}
                      </Typography>
                      <Box 
                        sx={{ 
                          height: 80, 
                          bgcolor: alpha(
                            attendance >= 90 ? theme.palette.success.main : 
                            attendance >= 80 ? theme.palette.warning.main : 
                            theme.palette.error.main, 
                            0.1
                          ),
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'end',
                          justifyContent: 'center',
                          mt: 1,
                          mb: 1,
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        <Box 
                          sx={{ 
                            height: `${(attendance / 100) * 80}px`, 
                            width: '100%',
                            background: `linear-gradient(135deg, ${
                              attendance >= 90 ? theme.palette.success.main : 
                              attendance >= 80 ? theme.palette.warning.main : 
                              theme.palette.error.main
                            } 0%, ${alpha(
                              attendance >= 90 ? theme.palette.success.main : 
                              attendance >= 80 ? theme.palette.warning.main : 
                              theme.palette.error.main, 0.8
                            )} 100%)`,
                            borderRadius: 2,
                            transition: 'all 0.5s ease',
                          }} 
                        />
                      </Box>
                      <Typography variant="caption" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                        {attendance}%
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Box>

        {/* Enhanced Top Performers */}
        <Box sx={{ flex: 1 }}>
          <Paper 
            sx={{ 
              p: 3,
              borderRadius: 3,
              background: theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
                : 'linear-gradient(135deg, #232526 0%, #414345 100%)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(circle at 10% 20%, ${alpha(theme.palette.warning.main, 0.1)} 0%, transparent 40%),
                  radial-gradient(circle at 80% 80%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 40%)
                `,
                pointerEvents: 'none',
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <EmojiEvents sx={{ color: 'warning.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Top Performing Classes
                </Typography>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {topPerformers.map((cls, index) => (
                    <Box 
                      key={cls.name}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        p: 2,
                        bgcolor: theme.palette.mode === 'light'
                          ? alpha(theme.palette.common.white, 0.7)
                          : alpha(theme.palette.background.paper, 0.7),
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.customShadows?.hover || `0 8px 16px ${alpha(theme.palette.common.black, 0.1)}`,
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          background: index === 0 
                            ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
                            : index === 1 
                            ? 'linear-gradient(135deg, #c0c0c0 0%, #e5e5e5 100%)'
                            : 'linear-gradient(135deg, #cd7f32 0%, #daa520 100%)',
                          color: index === 1 ? 'black' : 'white',
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                        }}
                      >
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {cls.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cls.students} students • {cls.improvement > 0 ? '+' : ''}{cls.improvement}% improvement
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          label={`${cls.attendance}%`} 
                          color={index === 0 ? 'warning' : 'default'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            background: index === 0 
                              ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
                              : undefined
                          }}
                        />
                        {index === 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                            <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
} 